import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { UserType } from '@/types/database';

// Gerarchia ruoli: admin > blog_editor > operatore_business/membro_forze_armate > utente
const ROLE_HIERARCHY: Record<UserType, number> = {
  utente: 1,
  operatore_business: 2,
  membro_forze_armate: 2,
  blog_editor: 3,
  admin: 4,
};

export const useUserRole = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<UserType | null>(null);
  const [superAdmin, setSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setUserRole(null);
        setSuperAdmin(false);
        setLoading(false);
        return;
      }

      try {
        // Fetch role and super-admin status in parallel
        const [roleResult, superAdminResult] = await Promise.all([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase.rpc('is_super_admin', { _user_id: user.id }),
        ]);

        if (roleResult.error) {
          console.error('Error fetching user role:', roleResult.error);
          setUserRole(null);
        } else if (roleResult.data) {
          setUserRole(roleResult.data.role as UserType);
        } else {
          setUserRole('utente');
        }

        setSuperAdmin(superAdminResult.data === true);
      } catch (error) {
        console.error('Error in fetchUserRole:', error);
        setUserRole(null);
        setSuperAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  // Verifica se l'utente ha un ruolo specifico
  const hasRole = (role: UserType): boolean => {
    return userRole === role;
  };

  // Verifica se l'utente è admin
  const isAdmin = (): boolean => {
    return userRole === 'admin';
  };

  // Verifica se l'utente è admin o blog_editor
  const isAdminOrEditor = (): boolean => {
    return userRole === 'admin' || userRole === 'blog_editor';
  };

  // Verifica se l'utente è operatore business o forze armate
  const isOperatoreOrForzeArmate = (): boolean => {
    return userRole === 'operatore_business' || userRole === 'membro_forze_armate';
  };

  // Verifica se l'utente è il super-admin (basato su funzione DB)
  const isSuperAdmin = (): boolean => {
    return superAdmin;
  };

  // Verifica se un altro utente è super-admin (via RPC)
  const checkIsSuperAdmin = async (targetUserId: string): Promise<boolean> => {
    try {
      const { data } = await supabase.rpc('is_super_admin', { _user_id: targetUserId });
      return data === true;
    } catch {
      return false;
    }
  };

  // Verifica se l'utente può gestire un determinato ruolo
  const canManageRole = (targetRole: UserType): boolean => {
    if (!userRole) return false;
    
    // Tutti gli admin possono gestire tutti i ruoli, incluso admin
    if (userRole === 'admin') {
      return true;
    }
    
    // Blog editor può gestire tutti i ruoli tranne admin
    if (userRole === 'blog_editor') {
      return targetRole !== 'admin';
    }
    
    return false;
  };

  // Verifica gerarchia: l'utente ha livello >= al ruolo richiesto
  const hasRoleLevel = (requiredRole: UserType | UserType[]): boolean => {
    if (!userRole) return false;
    
    const userLevel = ROLE_HIERARCHY[userRole];
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.some(r => userLevel >= ROLE_HIERARCHY[r]);
    }
    
    return userLevel >= ROLE_HIERARCHY[requiredRole];
  };

  // Ottieni ruoli disponibili per l'assegnazione
  const getAvailableRoles = (): UserType[] => {
    const baseRoles: UserType[] = ['utente', 'operatore_business', 'membro_forze_armate', 'blog_editor'];
    
    // Tutti gli admin possono assegnare qualsiasi ruolo, incluso admin
    if (userRole === 'admin') {
      return [...baseRoles, 'admin'];
    }
    
    return baseRoles;
  };

  return {
    userRole,
    loading,
    hasRole,
    isAdmin,
    isAdminOrEditor,
    isOperatoreOrForzeArmate,
    isSuperAdmin,
    checkIsSuperAdmin,
    canManageRole,
    hasRoleLevel,
    getAvailableRoles,
  };
};
